// ═══════════════════════════════════════════════════════════════
// BLACKBOARD — Training Plan (Individual User)
// Shows user info, compliance ring, assigned trainings,
// completion history, and position-based bulk assignment
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Text,
  TextInput,
  Button,
  Group,
  Badge,
  Modal,
  Textarea,
  Table,
  ActionIcon,
  Stack,
  Loader,
  Center,
  Tooltip,
  Divider,
  Checkbox,
  RingProgress,
} from '../../components/gab/GabComponents';
import { DateInput } from '../../components/gab/MantineDates';
import {
  IconArrowLeft,
  IconPlus,
  IconWand,
  IconTrash,
  IconSearch,
  IconCheck,
  IconAlertTriangle,
  IconClipboardList,
  IconHistory,
  IconCalendar,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useBlackboardStore } from '../../store/blackboardStore';
import { POSITION_MAP } from './utils/positions';
import dayjs from 'dayjs';

import type {
  Training,
  TrainingRecord,
  Assignment,
  PositionPlan,
  BBUser,
} from '../../store/blackboardStore';

// ── Constants ────────────────────────────────────────────────

const CATEGORY_COLORS: Record<string, string> = {
  'NIMS/ICS': 'var(--color-primary-700)',
  'Professional Development': 'var(--data-viz-650)',
  'Hazmat/Safety': '#F86A0B',
  'Health & Safety': 'var(--color-error)',
  Operations: '#03AC9E',
  'Community Programs': 'var(--color-success)',
  Weather: '#03AC9E',
  Communications: 'var(--data-viz-100)',
  Custom: '#939598',
};

// ── Helpers ──────────────────────────────────────────────────

function formatDate(d: string | null | undefined): string {
  if (!d) return '\u2014';
  return dayjs(d).format('MMM D, YYYY');
}

interface StatusInfo {
  label: string;
  color: string;
  key: string;
}

function getAssignmentStatus(
  assignment: Assignment,
  records: TrainingRecord[],
  _trainings: Training[],
): StatusInfo {
  const assignedIds = (assignment.trainingIds || '').split(',').map(s => s.trim()).filter(Boolean);

  // Check if any assigned training has a completed record
  const hasCompleted = assignedIds.some(tid =>
    records.some(r => r.trainingId === tid && r.completedDate),
  );

  if (hasCompleted) {
    return { label: 'Completed', color: '#16a34a', key: 'completed' };
  }

  if (!assignment.dueDate) {
    return { label: 'Assigned', color: '#4a82c4', key: 'assigned' };
  }

  const dueDate = dayjs(assignment.dueDate);
  const daysUntil = dueDate.diff(dayjs(), 'day');

  if (daysUntil < 0) {
    return { label: 'Overdue', color: '#B12525', key: 'overdue' };
  }
  if (daysUntil <= 30) {
    return { label: 'Due Soon', color: '#ea580c', key: 'due_soon' };
  }
  return { label: 'Assigned', color: '#4a82c4', key: 'assigned' };
}

function getRecordStatus(record: TrainingRecord): { label: string; color: string } {
  if (!record.completedDate) {
    return { label: 'Incomplete', color: '#B12525' };
  }
  if (!record.expiresDate) {
    return { label: 'Completed', color: '#16a34a' };
  }
  const daysUntil = dayjs(record.expiresDate).diff(dayjs(), 'day');
  if (daysUntil < 0) return { label: 'Expired', color: '#B12525' };
  if (daysUntil <= 30) return { label: 'Expiring', color: '#d97706' };
  return { label: 'Compliant', color: '#16a34a' };
}

function SectionHeader({ label }: { label: string }) {
  return (
    <Box mb="md">
      <Text tt="uppercase" fz={10} fw={700} c="dimmed" style={{ letterSpacing: 2 }}>
        {label}
      </Text>
      <Divider mt={6} />
    </Box>
  );
}

// ── Component ────────────────────────────────────────────────

export default function TrainingPlan() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const {
    users,
    trainings,
    trainingRecords,
    assignments,
    positionPlans,
    loading: storeLoading,
    loadUsers,
    loadTrainings,
    loadTrainingRecords,
    loadAssignments,
    loadPositionPlans,
    createAssignment,
    deleteAssignment,
  } = useBlackboardStore();

  const [initialLoad, setInitialLoad] = useState(true);

  // Assign modal
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignSearch, setAssignSearch] = useState('');
  const [selectedTrainingIds, setSelectedTrainingIds] = useState<string[]>([]);
  const [assignDueDate, setAssignDueDate] = useState<string | null>(null);
  const [assignNotes, setAssignNotes] = useState('');
  const [assigning, setAssigning] = useState(false);

  // Remove confirm
  const [removeTarget, setRemoveTarget] = useState<Assignment | null>(null);
  const [removing, setRemoving] = useState(false);

  // Bulk assign
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [bulkAssigningPosition, setBulkAssigningPosition] = useState(false);

  // ── Load data ──────────────────────────────────────────────

  useEffect(() => {
    async function load() {
      await Promise.all([
        loadUsers(),
        loadTrainings(),
        loadTrainingRecords(),
        loadAssignments(),
        loadPositionPlans(),
      ]);
      setInitialLoad(false);
    }
    load();
  }, [loadUsers, loadTrainings, loadTrainingRecords, loadAssignments, loadPositionPlans]);

  // ── Derived data ───────────────────────────────────────────

  const student: BBUser | undefined = useMemo(
    () => users.find(u => String(u.id) === userId),
    [users, userId],
  );

  const userRecords = useMemo(
    () => trainingRecords.filter(r => r.userId === userId),
    [trainingRecords, userId],
  );

  const userAssignments = useMemo(
    () => assignments.filter(a => a.userId === userId),
    [assignments, userId],
  );

  const activeTrainings = useMemo(
    () => trainings.filter(t => t.active === 'true' || t.active === '1'),
    [trainings],
  );

  // Position plan for this user
  const positionPlan: PositionPlan | undefined = useMemo(() => {
    if (!student?.position) return undefined;
    return positionPlans.find(
      p => (p.position || '').toLowerCase() === (student.position || '').toLowerCase(),
    );
  }, [student, positionPlans]);

  // All training IDs already assigned to this user
  const assignedTrainingIds = useMemo(() => {
    const ids = new Set<string>();
    userAssignments.forEach(a => {
      (a.trainingIds || '').split(',').forEach(tid => {
        const trimmed = tid.trim();
        if (trimmed) ids.add(trimmed);
      });
    });
    return ids;
  }, [userAssignments]);

  // Compliance calculation
  const compliance = useMemo(() => {
    if (userAssignments.length === 0) return { percent: 100, completed: 0, total: 0 };
    let completed = 0;
    userAssignments.forEach(a => {
      const status = getAssignmentStatus(a, userRecords, activeTrainings);
      if (status.key === 'completed') completed++;
    });
    return {
      percent: Math.round((completed / userAssignments.length) * 100),
      completed,
      total: userAssignments.length,
    };
  }, [userAssignments, userRecords, activeTrainings]);

  // Trainings available for assignment
  const availableTrainings = useMemo(() => {
    return activeTrainings.filter(t => {
      if (assignedTrainingIds.has(String(t.id))) return false;
      if (assignSearch) {
        return (t.name || '').toLowerCase().includes(assignSearch.toLowerCase());
      }
      return true;
    });
  }, [activeTrainings, assignedTrainingIds, assignSearch]);

  // Position-required trainings
  const positionRequiredTrainings = useMemo(() => {
    if (!student?.position || !positionPlan) return [];
    const planIds = new Set((positionPlan.trainingIds || '').split(',').map(s => s.trim()).filter(Boolean));
    return activeTrainings.filter(t => planIds.has(String(t.id)));
  }, [student, positionPlan, activeTrainings]);

  const missingPositionTrainings = useMemo(
    () => positionRequiredTrainings.filter(t => !assignedTrainingIds.has(String(t.id))),
    [positionRequiredTrainings, assignedTrainingIds],
  );

  // ── Handlers ───────────────────────────────────────────────

  const toggleTrainingSelection = (trainingId: string) => {
    setSelectedTrainingIds(prev =>
      prev.includes(trainingId) ? prev.filter(id => id !== trainingId) : [...prev, trainingId],
    );
  };

  const handleAssign = async () => {
    if (selectedTrainingIds.length === 0) {
      notifications.show({ title: 'Warning', message: 'Select at least one training to assign.', color: '#C68700' });
      return;
    }
    if (!assignDueDate) {
      notifications.show({ title: 'Warning', message: 'Please select a due date.', color: '#C68700' });
      return;
    }

    setAssigning(true);
    try {
      await createAssignment({
        userId: userId!,
        trainingIds: selectedTrainingIds.join(','),
        dueDate: dayjs(assignDueDate).format('YYYY-MM-DD'),
        notes: assignNotes.trim(),
      });
      notifications.show({ title: 'Success', message: `${selectedTrainingIds.length} training(s) assigned.`, color: 'var(--color-success)' });
      setAssignModalOpen(false);
      setSelectedTrainingIds([]);
      setAssignDueDate(null);
      setAssignNotes('');
      setAssignSearch('');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notifications.show({ title: 'Error', message: 'Failed to assign trainings: ' + msg, color: 'var(--color-error)' });
    } finally {
      setAssigning(false);
    }
  };

  const handleRemoveAssignment = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await deleteAssignment(removeTarget.id);
      notifications.show({ title: 'Success', message: 'Assignment removed.', color: 'var(--color-success)' });
      setRemoveTarget(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notifications.show({ title: 'Error', message: 'Failed to remove assignment: ' + msg, color: 'var(--color-error)' });
    } finally {
      setRemoving(false);
    }
  };

  const handleBulkAssignRequired = async () => {
    if (!student) return;
    const role = student.role || 'student';
    const requiredTrainings = activeTrainings.filter(t => {
      if (assignedTrainingIds.has(String(t.id))) return false;
      return t.requiredForRoles && t.requiredForRoles.split(',').map(s => s.trim()).includes(role);
    });

    if (requiredTrainings.length === 0) {
      notifications.show({ title: 'Info', message: 'All required trainings are already assigned.', color: 'var(--color-primary-700)' });
      return;
    }

    setBulkAssigning(true);
    try {
      const defaultDue = dayjs().add(90, 'day').format('YYYY-MM-DD');
      await createAssignment({
        userId: userId!,
        trainingIds: requiredTrainings.map(t => String(t.id)).join(','),
        dueDate: defaultDue,
        notes: 'Auto-assigned based on role requirements',
      });
      notifications.show({ title: 'Success', message: `${requiredTrainings.length} required training(s) assigned.`, color: 'var(--color-success)' });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notifications.show({ title: 'Error', message: 'Failed to bulk assign: ' + msg, color: 'var(--color-error)' });
    } finally {
      setBulkAssigning(false);
    }
  };

  const handleBulkAssignPositionRequired = async () => {
    if (!student?.position || missingPositionTrainings.length === 0) {
      notifications.show({ title: 'Info', message: 'All position-required trainings are already assigned.', color: 'var(--color-primary-700)' });
      return;
    }

    setBulkAssigningPosition(true);
    try {
      const defaultDue = dayjs().add(90, 'day').format('YYYY-MM-DD');
      const posInfo = POSITION_MAP[student.position];
      await createAssignment({
        userId: userId!,
        trainingIds: missingPositionTrainings.map(t => String(t.id)).join(','),
        dueDate: defaultDue,
        notes: `Auto-assigned based on position requirements (${posInfo?.label || student.position})`,
      });
      notifications.show({
        title: 'Success',
        message: `${missingPositionTrainings.length} position-required training(s) assigned.`,
        color: 'var(--color-success)',
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notifications.show({ title: 'Error', message: 'Failed to bulk assign: ' + msg, color: 'var(--color-error)' });
    } finally {
      setBulkAssigningPosition(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────

  if (initialLoad || storeLoading) {
    return (
      <Center h={400}>
        <Loader color="var(--color-primary-700)" />
      </Center>
    );
  }

  if (!student) {
    return (
      <Center h={400}>
        <Text c="dimmed">User not found.</Text>
      </Center>
    );
  }

  const studentName =
    `${student.firstName || ''} ${student.lastName || ''}`.trim() || student.email || 'Unknown';
  const posInfo = student.position ? POSITION_MAP[student.position] : undefined;

  return (
    <Box>
      {/* Header */}
      <Group justify="space-between" align="flex-start" mb="lg">
        <Box>
          <Button
            variant="subtle"
            color="#939598"
            size="compact-sm"
            leftSection={<IconArrowLeft size={12} />}
            onClick={() => navigate('/settings/users')}
            mb="xs"
            px={0}
          >
            Users
          </Button>
          <Group gap="md" align="center">
            <Box>
              <Text size="xl" fw={700} c="#1a365d">
                {studentName}
              </Text>
              <Text size="sm" c="dimmed">
                {student.email || '\u2014'}
              </Text>
            </Box>
            {student.role && (
              <Badge variant="light" size="sm" color="var(--color-primary-700)">
                {student.role}
              </Badge>
            )}
            {posInfo && (
              <Badge
                variant="light"
                size="sm"
                styles={{
                  root: { backgroundColor: posInfo.color + '18', color: posInfo.color },
                }}
              >
                {posInfo.label}
              </Badge>
            )}
          </Group>
        </Box>

        <Group gap="md" align="center">
          <Box style={{ textAlign: 'center' }}>
            <RingProgress
              size={64}
              thickness={6}
              roundCaps
              sections={[
                {
                  value: compliance.percent,
                  color:
                    compliance.percent >= 80
                      ? '#16a34a'
                      : compliance.percent >= 50
                        ? '#d97706'
                        : '#B12525',
                },
              ]}
              label={
                <Text ta="center" size="xs" fw={700}>
                  {compliance.percent}%
                </Text>
              }
            />
            <Text size="xs" c="dimmed" mt={2}>
              {compliance.completed}/{compliance.total} Complete
            </Text>
          </Box>
        </Group>
      </Group>

      {/* Position Required Trainings */}
      {student.position && positionRequiredTrainings.length > 0 && posInfo && (
        <Paper
          withBorder
          shadow="sm"
          radius="md"
          p="md"
          mb="lg"
          style={{ borderColor: posInfo.color + '40' }}
        >
          <Group justify="space-between" align="center" mb={missingPositionTrainings.length > 0 ? 'sm' : 0}>
            <Group gap="sm">
              <posInfo.icon size={14} color={posInfo.color} />
              <Text size="sm" fw={600} c={posInfo.color}>
                {posInfo.label} Position Requirements
              </Text>
              <Badge variant="light" size="sm" color="#939598">
                {positionRequiredTrainings.length - missingPositionTrainings.length}/
                {positionRequiredTrainings.length} assigned
              </Badge>
            </Group>
            {missingPositionTrainings.length > 0 && (
              <Button
                variant="light"
                size="compact-sm"
                styles={{ root: { backgroundColor: posInfo.color + '18', color: posInfo.color } }}
                leftSection={<IconWand size={11} />}
                onClick={handleBulkAssignPositionRequired}
                loading={bulkAssigningPosition}
              >
                Assign Position Required ({missingPositionTrainings.length})
              </Button>
            )}
          </Group>
          {missingPositionTrainings.length > 0 ? (
            <Box>
              <Text size="xs" c="dimmed" mb={4}>
                Missing trainings for this position:
              </Text>
              <Group gap={4}>
                {missingPositionTrainings.map(t => (
                  <Badge key={t.id} variant="light" size="xs" color="#939598">
                    {t.name}
                  </Badge>
                ))}
              </Group>
            </Box>
          ) : (
            <Text size="xs" c="dimmed" mt={4}>
              All position-required trainings are assigned.
            </Text>
          )}
        </Paper>
      )}

      {/* Assigned Training Plan */}
      <SectionHeader label="Assigned Training Plan" />

      <Group justify="space-between" mb="md">
        <Text size="sm" c="dimmed">
          {userAssignments.length} assignment{userAssignments.length !== 1 ? 's' : ''}
        </Text>
        <Group gap="xs">
          <Button
            variant="outline"
            color="var(--color-primary-700)"
            size="sm"
            leftSection={<IconWand size={12} />}
            onClick={handleBulkAssignRequired}
            loading={bulkAssigning}
          >
            Assign All Required
          </Button>
          <Button
            color="var(--color-primary-700)"
            size="sm"
            leftSection={<IconPlus size={12} />}
            onClick={() => {
              setSelectedTrainingIds([]);
              setAssignDueDate(null);
              setAssignNotes('');
              setAssignSearch('');
              setAssignModalOpen(true);
            }}
          >
            Assign Training
          </Button>
        </Group>
      </Group>

      <Paper withBorder shadow="sm" radius="md" p={0} mb="xl" style={{ overflow: 'auto' }}>
        {userAssignments.length === 0 ? (
          <Center p="xl">
            <Box style={{ textAlign: 'center' }}>
              <IconClipboardList size={32} color="#868e96" style={{ marginBottom: 8 }} />
              <Text c="dimmed" size="sm">
                No trainings assigned yet.
              </Text>
            </Box>
          </Center>
        ) : (
          <Table highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Trainings</Table.Th>
                <Table.Th>Due Date</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Notes</Table.Th>
                <Table.Th style={{ width: 60 }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {userAssignments.map(a => {
                const status = getAssignmentStatus(a, userRecords, activeTrainings);
                const tids = (a.trainingIds || '').split(',').map(s => s.trim()).filter(Boolean);
                const trainingNames = tids
                  .map(tid => activeTrainings.find(t => String(t.id) === tid)?.name || tid)
                  .join(', ');

                return (
                  <Table.Tr key={a.id}>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {trainingNames || '\u2014'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{formatDate(a.dueDate)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        variant="light"
                        size="sm"
                        style={{ backgroundColor: status.color + '18', color: status.color }}
                      >
                        {status.label}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {a.notes || '\u2014'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Tooltip label="Remove assignment">
                        <ActionIcon variant="subtle" color="var(--color-error)" size="sm" onClick={() => setRemoveTarget(a)}>
                          <IconTrash size={12} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      {/* Completion History */}
      <SectionHeader label="Completion History" />

      <Paper withBorder shadow="sm" radius="md" p={0} mb="xl" style={{ overflow: 'auto' }}>
        {userRecords.length === 0 ? (
          <Center p="xl">
            <Box style={{ textAlign: 'center' }}>
              <IconHistory size={32} color="#868e96" style={{ marginBottom: 8 }} />
              <Text c="dimmed" size="sm">
                No training records found for this user.
              </Text>
            </Box>
          </Center>
        ) : (
          <Table highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Training</Table.Th>
                <Table.Th>Completed Date</Table.Th>
                <Table.Th>Expires Date</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Source</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {userRecords.map(r => {
                const status = getRecordStatus(r);
                const source = r.certificateId ? 'Classroom' : 'Self-Reported';
                return (
                  <Table.Tr key={r.id}>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {r.trainingName || '\u2014'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{formatDate(r.completedDate)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{formatDate(r.expiresDate)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        variant="light"
                        size="sm"
                        style={{ backgroundColor: status.color + '18', color: status.color }}
                      >
                        {status.label}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" size="sm" color={r.certificateId ? '#03AC9E' : '#939598'}>
                        {source}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      {/* Assign Training Modal */}
      <Modal
        opened={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        title={
          <Group gap="xs">
            <IconPlus size={16} color="#4a82c4" />
            <Text fw={600}>Assign Training</Text>
          </Group>
        }
        size="lg"
        centered
        overlayProps={{ blur: 3 }}
      >
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Select trainings to assign to <Text span fw={600}>{studentName}</Text>.
          </Text>

          <TextInput
            placeholder="Search trainings..."
            size="sm"
            leftSection={<IconSearch size={13} />}
            value={assignSearch}
            onChange={e => setAssignSearch(e.target.value)}
          />

          <Box
            style={{
              maxHeight: 280,
              overflowY: 'auto',
              border: '1px solid #dee2e6',
              borderRadius: 4,
            }}
          >
            {availableTrainings.length === 0 ? (
              <Center p="lg">
                <Text size="sm" c="dimmed">
                  {assignSearch ? 'No matching trainings found.' : 'All trainings are already assigned.'}
                </Text>
              </Center>
            ) : (
              <Stack gap={0}>
                {availableTrainings.map(t => (
                  <Group
                    key={t.id}
                    px="sm"
                    py={8}
                    style={{
                      cursor: 'pointer',
                      borderBottom: '1px solid #f1f3f5',
                      backgroundColor: selectedTrainingIds.includes(String(t.id))
                        ? '#e7f5ff'
                        : 'transparent',
                    }}
                    onClick={() => toggleTrainingSelection(String(t.id))}
                  >
                    <Checkbox
                      size="sm"
                      checked={selectedTrainingIds.includes(String(t.id))}
                      onChange={() => toggleTrainingSelection(String(t.id))}
                      styles={{ input: { cursor: 'pointer' } }}
                    />
                    <Box style={{ flex: 1 }}>
                      <Text size="sm" fw={500}>
                        {t.name}
                      </Text>
                      {t.description && (
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {t.description}
                        </Text>
                      )}
                    </Box>
                    <Badge variant="light" size="sm" color={CATEGORY_COLORS[t.category] || '#939598'}>
                      {t.category || 'Custom'}
                    </Badge>
                  </Group>
                ))}
              </Stack>
            )}
          </Box>

          {selectedTrainingIds.length > 0 && (
            <Text size="xs" c="var(--color-primary-700)" fw={500}>
              {selectedTrainingIds.length} training{selectedTrainingIds.length !== 1 ? 's' : ''} selected
            </Text>
          )}

          <DateInput
            label="Due Date"
            placeholder="Select due date for all selected trainings"
            size="sm"
            required
            value={assignDueDate}
            onChange={setAssignDueDate}
            minDate={dayjs().toDate()}
            leftSection={<IconCalendar size={13} />}
          />

          <Textarea
            label="Notes"
            placeholder="Optional notes for this assignment..."
            size="sm"
            minRows={2}
            value={assignNotes}
            onChange={e => setAssignNotes(e.target.value)}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" size="sm" onClick={() => setAssignModalOpen(false)}>
              Cancel
            </Button>
            <Button
              color="var(--color-primary-700)"
              size="sm"
              onClick={handleAssign}
              loading={assigning}
              disabled={selectedTrainingIds.length === 0}
              leftSection={<IconCheck size={12} />}
            >
              Assign {selectedTrainingIds.length > 0 ? `(${selectedTrainingIds.length})` : ''}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Remove Assignment Confirmation Modal */}
      <Modal
        opened={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        title={
          <Group gap="xs">
            <IconAlertTriangle size={16} color="#B12525" />
            <Text fw={600}>Remove Assignment</Text>
          </Group>
        }
        size="sm"
        centered
        overlayProps={{ blur: 3 }}
      >
        <Text size="sm" mb="lg">
          Are you sure you want to remove this assignment? This will not delete any existing
          completion records.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" size="sm" onClick={() => setRemoveTarget(null)}>
            Cancel
          </Button>
          <Button color="var(--color-error)" size="sm" onClick={handleRemoveAssignment} loading={removing}>
            Remove
          </Button>
        </Group>
      </Modal>
    </Box>
  );
}
